import { useCallback, useContext, useEffect, useRef } from 'preact/hooks'
import { AthosBeacon, claimBeaconEvent } from '../classes/athos-beacon'
import { createContext } from 'preact'
import { useAthosContext } from '../fetchers/provider'
import { useIntersectionObserver } from '@/components/hooks/useIntersectionObserver'

const defaultValue = {
  analytics: undefined,
  responseId: undefined,
  results: undefined
}

const BeaconAnalyticsContext = createContext(defaultValue)

export function useBeaconAnalytics() {
  const context = useContext(BeaconAnalyticsContext)
  return context ?? defaultValue
}

export function BeaconAnalyticsProvider({ type = 'recommendations', profile, results: resultsOverride, children }) {
  const { current: analytics } = useRef(new AthosBeacon(type, profile))
  const { results: contextResults } = useAthosContext()
  const results = resultsOverride ?? contextResults
  const responseId = results?.responseId

  if (responseId) analytics.setResponseId(responseId)

  const value = { analytics, responseId, results }

  return <BeaconAnalyticsContext.Provider value={value}>{children}</BeaconAnalyticsContext.Provider>
}

export function useBeaconLogRender({ enabled = true } = {}) {
  const { analytics, responseId, results: beaconResults } = useBeaconAnalytics()
  const { results: contextResults } = useAthosContext()
  const results = beaconResults ?? contextResults

  useEffect(() => {
    if (!enabled || !analytics) return
    if (!responseId || !results?.results?.length) return
    if (!claimBeaconEvent(analytics, responseId, 'render')) return
    analytics.render()
  }, [enabled, analytics, responseId, results?.results?.length])
}

export function useBeaconLogImpressions(ref, { enabled = true } = {}) {
  const { analytics, responseId, results: beaconResults } = useBeaconAnalytics()
  const { results: contextResults } = useAthosContext()
  const results = beaconResults ?? contextResults
  const { setNode, entry } = useIntersectionObserver({ threshold: 0.1 })

  useEffect(() => {
    if (ref) setNode(ref)
  }, [ref])

  useEffect(() => {
    if (!enabled || !analytics) return
    if (!entry?.isIntersecting) return
    if (!responseId || !results?.results?.length) return
    if (!claimBeaconEvent(analytics, responseId, 'impression')) return
    analytics.impression(results.results)
  }, [enabled, analytics, entry, responseId, results?.results?.length])
}

export function useBeaconLogClick() {
  const { analytics } = useBeaconAnalytics()

  return useCallback((result, options) => {
    if (!analytics) return
    analytics.clickthrough(result, options)
  }, [analytics])
}

export function useBeaconLogAddToCart() {
  const { analytics } = useBeaconAnalytics()

  return useCallback((result, variant, options) => {
    if (!analytics) return
    analytics.addtocart(result, variant, options)
  }, [analytics])
}

export function useBeaconLogRedirect() {
  const { analytics } = useBeaconAnalytics()

  return useCallback((redirectUrl) => {
    if (!analytics) return
    analytics.redirect(redirectUrl)
  }, [analytics])
}
